import { useState, useEffect, useRef } from "react";
import { IoMdCheckmark } from "react-icons/io";
import "react-datepicker/dist/react-datepicker.css";
import styles from "../../styles/__labDialogAddEditTest.module.scss";
import { useTranslation } from "react-i18next";
import { toast } from '../toast';
import axios from "axios";

// Service code format: uppercase letters/digits in hyphen-separated groups
// (e.g. PHYS-10234). No leading/trailing/double hyphens, spaces or symbols.
const SERVICE_CODE_REGEX = /^[A-Z0-9]+(-[A-Z0-9]+)*$/;
const SERVICE_CODE_MIN = 3;
const SERVICE_CODE_MAX = 20;

const validateServiceCode = (code) => {
  const value = (code || "").trim();
  if (!value) return "Service code is required";
  if (value.length < SERVICE_CODE_MIN || value.length > SERVICE_CODE_MAX) {
    return `Service code must be ${SERVICE_CODE_MIN}-${SERVICE_CODE_MAX} characters`;
  }
  if (!SERVICE_CODE_REGEX.test(value)) {
    return "Use uppercase letters, numbers and hyphens only (e.g. PHYS-10234)";
  }
  return "";
};

// Center prefix = first 3 letters of the center name, uppercased
// (e.g. "Sofia" -> "SOF", "Maven Physio Therapy" -> "MAV").
const deriveServicePrefix = (centerName) =>
  (centerName || "").replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase();

// Create authenticated axios instance (same pattern as profile page)
const createAxiosInstance = () => {
  const token = sessionStorage.getItem("usr_1a2b3c");
  return axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  });
};

export default function PhysioDialogAddEditTest({
  open,
  onClose,
  type = "add",
  selectedTest = null,
  onSuccess,
}) {
  const { t } = useTranslation();

  const [serviceCode, setServiceCode] = useState("");
  const [serviceCodeError, setServiceCodeError] = useState("");
  // 3-letter center prefix (e.g. "MAV"); the "MAV-" segment is locked/uneditable.
  const [servicePrefix, setServicePrefix] = useState("");
  // Tracks whether the user has manually edited the code, so the async
  // auto-generation doesn't overwrite their input when it resolves.
  const manuallyEditedRef = useRef(false);
  const [serviceName, setServiceName] = useState("");
  const [serviceNameArabic, setServiceNameArabic] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState(true);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Fetch categories on mount
  useEffect(() => {
    if (!open) return;
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const axiosInstance = createAxiosInstance();
        const response = await axiosInstance.get(
          `/api/v1/physiotherapist/services/categories`
        );
        if (response?.data?.status && response.data.data) {
          // Response format: [{ id, name, nameArabic }]
          setCategories(Array.isArray(response.data.data) ? response.data.data : []);
        }
      } catch (err) {
        console.warn("Failed to fetch categories:", err);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, [open]);

  // On open: derive the center prefix from the profile (used to lock the input
  // prefix), and on ADD fetch the next SEQUENTIAL service code from the backend
  // (e.g. "MAV-0001"). Uniqueness is guaranteed per center by the backend.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const init = async () => {
      const axiosInstance = createAxiosInstance();

      // 1) Center prefix from the profile.
      let prefix = "";
      try {
        const profileRes = await axiosInstance.get(`/api/v1/physiotherapist/profile`);
        prefix = deriveServicePrefix(profileRes?.data?.data?.center_name_english || "");
      } catch (err) {
        console.warn("Failed to fetch center profile for service prefix:", err);
      }
      if (cancelled) return;
      setServicePrefix(prefix);

      // 2) On ADD, ask the backend for the next sequential code for this center.
      if (type === "add") {
        try {
          const url = `/api/v1/physiotherapist/services/code/generate${prefix ? `?prefix=${encodeURIComponent(prefix)}` : ""}`;
          const codeRes = await axiosInstance.get(url);
          const nextCode = codeRes?.data?.data?.serviceCode || "";
          if (!cancelled && nextCode && !manuallyEditedRef.current) {
            setServiceCode(nextCode);
          }
        } catch (err) {
          console.warn("Failed to generate next service code:", err);
        }
      }
    };
    init();
    return () => {
      cancelled = true;
    };
  }, [open, type]);

  // Populate fields when editing
  useEffect(() => {
    if (type === "edit" && selectedTest) {
      setServiceCode(selectedTest.serviceCode || selectedTest.service_code || "");
      setServiceCodeError("");
      setServiceName(selectedTest.testName || selectedTest.serviceName || "");
      setServiceNameArabic(selectedTest.testNameArabic || selectedTest.serviceNameArabic || "");
      setCategoryId(selectedTest.serviceCategoryId?.toString() || selectedTest.categoryId?.toString() || "");
      // Remove decimals from price - display as whole number
      const rawPrice = selectedTest.price?.toString() || "";
      setPrice(rawPrice ? String(Math.round(Number(rawPrice))) : "");
      setStatus(selectedTest.status === "Active" || selectedTest.status === "ACTIVE");
    } else if (type === "add") {
      // Start empty; the async effect fills the next sequential code from the
      // backend once it resolves.
      manuallyEditedRef.current = false;
      setServiceCode("");
      setServiceCodeError("");
      setServiceName("");
      setServiceNameArabic("");
      setCategoryId("");
      setPrice("");
      setStatus(true);
    }
  }, [open, type, selectedTest]);

  useEffect(() => {
    if (open) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => document.body.classList.remove("modal-open");
  }, [open]);

  if (!open) return null;

  const handleSave = async () => {
    const codeError = validateServiceCode(serviceCode);
    if (codeError) {
      setServiceCodeError(codeError);
      return;
    }
    if (!serviceName.trim()) {
      toast.error("Service name (English) is required");
      return;
    }
    if (!serviceNameArabic.trim()) {
      toast.error("Service name (Arabic) is required");
      return;
    }
    if (!categoryId) {
      toast.error("Category is required");
      return;
    }
    if (!price || parseFloat(String(price).replace(/,/g, "")) <= 0) {
      toast.error("Valid price is required");
      return;
    }

    setLoading(true);
    try {
      const axiosInstance = createAxiosInstance();

      const serviceData = {
        serviceCode: serviceCode.trim(),
        serviceName: serviceName.trim(),
        serviceNameArabic: serviceNameArabic.trim() || undefined,
        serviceCategoryId: categoryId ? parseInt(categoryId) : undefined,
        price: Math.round(Number(String(price).replace(/[^0-9]/g, ""))),
        // Location is no longer captured at service level (kept null); the
        // preferred location is applied per-order in the appointment detail.
        location: null,
        status: status ? "ACTIVE" : "INACTIVE",
      };

      let response;
      if (type === "edit" && selectedTest?.id) {
        response = await axiosInstance.put(
          `/api/v1/physiotherapist/services/${selectedTest.id}`,
          serviceData
        );
      } else {
        response = await axiosInstance.post(
          `/api/v1/physiotherapist/services`,
          serviceData
        );
      }

      if (response?.data?.status) {
        toast.success(type === "edit" ? "Service updated successfully" : "Service created successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response?.data?.message || "Failed to save service");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to save service");
    } finally {
      setLoading(false);
    }
  };

  // The locked, non-editable prefix segment (e.g. "MAV-"). On ADD it's always
  // the center prefix; on EDIT it's only locked when the existing code already
  // uses that prefix (so legacy codes with a different prefix aren't mangled).
  const lockedPrefix = servicePrefix
    ? (type === "add" || serviceCode.startsWith(`${servicePrefix}-`) ? `${servicePrefix}-` : "")
    : "";

  const handleServiceCodeChange = (e) => {
    // Restrict to uppercase letters, numbers and hyphens.
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "");
    let next = raw;
    if (lockedPrefix) {
      // Keep everything after the locked prefix as the editable suffix; if the
      // user edited into/deleted the prefix, snap it back and preserve their suffix.
      let suffix = raw.startsWith(lockedPrefix)
        ? raw.slice(lockedPrefix.length)
        : raw.replace(new RegExp(`^${servicePrefix}?-*`), "");
      suffix = suffix.replace(/^-+/, ""); // no leading hyphen right after the prefix
      next = lockedPrefix + suffix;
    }
    manuallyEditedRef.current = true;
    setServiceCode(next);
    if (serviceCodeError) setServiceCodeError(validateServiceCode(next));
  };

  return (
    <div className={`${styles["labDialog_wrapper"]}`}>
      <div className={`${styles["dialog-overlay"]}`}>
        <div className={styles["dialog-box"]}>
          {/* Close Button (X) */}
          <button className={styles["dialog-close"]} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13.5525 12.0002L17.8525 7.71019C18.0408 7.52188 18.1466 7.26649 18.1466 7.00019C18.1466 6.73388 18.0408 6.47849 17.8525 6.29019C17.6642 6.10188 17.4088 5.99609 17.1425 5.99609C16.8762 5.99609 16.6208 6.10188 16.4325 6.29019L12.1425 10.5902L7.85252 6.29019C7.66421 6.10188 7.40882 5.99609 7.14252 5.99609C6.87622 5.99609 6.62082 6.10188 6.43252 6.29019C6.24421 6.47849 6.13843 6.73388 6.13843 7.00019C6.13843 7.26649 6.24421 7.52188 6.43252 7.71019L10.7325 12.0002L6.43252 16.2902C6.33879 16.3831 6.26439 16.4937 6.21363 16.6156C6.16286 16.7375 6.13672 16.8682 6.13672 17.0002C6.13672 17.1322 6.16286 17.2629 6.21363 17.3848C6.26439 17.5066 6.33879 17.6172 6.43252 17.7102C6.52548 17.8039 6.63608 17.8783 6.75794 17.9291C6.8798 17.9798 7.01051 18.006 7.14252 18.006C7.27453 18.006 7.40523 17.9798 7.52709 17.9291C7.64895 17.8783 7.75955 17.8039 7.85252 17.7102L12.1425 13.4102L16.4325 17.7102C16.5255 17.8039 16.6361 17.8783 16.7579 17.9291C16.8798 17.9798 17.0105 18.006 17.1425 18.006C17.2745 18.006 17.4052 17.9798 17.5271 17.9291C17.649 17.8783 17.7596 17.8039 17.8525 17.7102C17.9462 17.6172 18.0206 17.5066 18.0714 17.3848C18.1222 17.2629 18.1483 17.1322 18.1483 17.0002C18.1483 16.8682 18.1222 16.7375 18.0714 16.6156C18.0206 16.4937 17.9462 16.3831 17.8525 16.2902L13.5525 12.0002Z" fill="currentcolor"/>
            </svg>
          </button>

          {/* Title */}
          <div className={styles["mb-24"]}>
            <h2 className={styles["dialog-title"]}>
              {type === "add" ? "Add New Service" : "Edit Service"}
            </h2>
          </div>

          <div className={styles["form-design"]}>
            {/* Service Code */}
            <div className={styles["form-group"]}>
              <label>Service Code<span className={styles["required-star"]}>*</span></label>
              <input
                type="text"
                placeholder={`e.g. ${servicePrefix || "PHYS"}-10234`}
                value={serviceCode}
                maxLength={SERVICE_CODE_MAX}
                onChange={handleServiceCodeChange}
                onKeyDown={(e) => {
                  // Block Backspace/Delete when the caret is within the locked prefix.
                  if (!lockedPrefix) return;
                  const el = e.target;
                  const start = el.selectionStart ?? 0;
                  const end = el.selectionEnd ?? 0;
                  if (
                    (e.key === "Backspace" && start <= lockedPrefix.length && start === end) ||
                    (e.key === "Delete" && start < lockedPrefix.length) ||
                    (start < lockedPrefix.length && end <= lockedPrefix.length &&
                      (e.key === "Backspace" || e.key === "Delete"))
                  ) {
                    e.preventDefault();
                  }
                }}
                onBlur={() => setServiceCodeError(validateServiceCode(serviceCode))}
                className={styles["text-input"]}
              />
              {serviceCodeError && (
                <span className={styles["rule-invalid"]}>{serviceCodeError}</span>
              )}
            </div>

            {/* Service Name (English) */}
            <div className={styles["form-group"]}>
              <label>Service Name (English)<span className={styles["required-star"]}>*</span></label>
              <input
                type="text"
                placeholder="Enter service name"
                value={serviceName}
                onChange={(e) => setServiceName(e.target.value)}
                className={styles["text-input"]}
              />
            </div>

            {/* Service Name (Arabic) */}
            <div className={styles["form-group"]}>
              <label>Service Name (Arabic)<span className={styles["required-star"]}>*</span></label>
              <input
                type="text"
                placeholder="أدخل اسم الخدمة"
                value={serviceNameArabic}
                onChange={(e) => setServiceNameArabic(e.target.value)}
                className={`text-end ${styles["text-input"]}`}
                style={{ direction: "rtl" }}
              />
            </div>

            {/* Category */}
            <div className={styles["form-group"]}>
              <label>Category<span className={styles["required-star"]}>*</span></label>
              <select
                className={styles["select-input"]}
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                disabled={categoriesLoading}
              >
                <option value="">
                  {categoriesLoading ? "Loading..." : "Select category"}
                </option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id.toString()}>
                    {cat.name}{cat.nameArabic ? ` - ${cat.nameArabic}` : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Price (IQD) */}
            <div className={styles["form-group"]}>
              <label>Price (IQD)<span className={styles["required-star"]}>*</span></label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="eg. 10000"
                value={price}
                onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
                className={styles["text-input"]}
              />
            </div>
          </div>

          {/* Footer */}
          <div className={`${styles["dialog-footer"]}`}>
            <button className={`${styles["btn-outlined-1"]}`} onClick={onClose}>
              Cancel
            </button>
            <button
              className={styles["btn-1"]}
              onClick={handleSave}
              disabled={
                loading ||
                !!validateServiceCode(serviceCode) ||
                !serviceName.trim() ||
                !serviceNameArabic.trim() ||
                !categoryId ||
                !price
              }
              style={{ opacity: loading ? 0.6 : 1 }}
            >
              <IoMdCheckmark /> {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
